<template>
    <div class="">
        <p v-if="plans.length == 0" class="fs-3 mt-3 acenter">
            <CompassButton
                :label="$t.__('preferences.Dunning.Dunning.start_automatic_dunning')"
                :loading="loading"
                @click="initFirstPlan"
            />
        </p>

        <div v-else>
            <dunning-enable
                :enabled="enabled"
                :enableLabel="$t.__('preferences.Dunning.activated')"
                :disableLabel="$t.__('preferences.Dunning.disabled')"
                :description="$t.__('preferences.Dunning.activation_of_automatic_restart')"
                @update:enabled="confirmationStatus = true"
            />

            <CompassDialog
                :visible.sync="confirmationStatus"
                size="small"
                :title="confirmationTitle"
                class="dunning-modal"
            >
                <template #default>
                    <div class="dialog__default">
                        <p class="wordbreak typographies-paragraph-medium-regular">
                            {{ confirmationMessage }}
                        </p>
                    </div>
                </template>
                <template #footer>
                    <div class="dialog__footer">
                        <CompassButton
                            :label="$t.__('common.actions.cancel')"
                            type="secondary"
                            @click="confirmationStatus = false"
                        />
                        <CompassButton
                            :label="$t.__('common.actions.confirm')"
                            type="primary"
                            :loading="loading"
                            class="ml-3"
                            @click="confirmToggle()"
                        />
                    </div>
                </template>
            </CompassDialog>

            <p class="well fs-3 pb-4 pt-3">
                <el-select v-model="defaultPlanId" class="fright w-5" @change="setDefault">
                    <el-option :label="$t.__('preferences.Dunning.Dunning.none')" :value="0">
                    </el-option>
                    <el-option
                        v-for="dunning in plans"
                        :key="dunning.id"
                        :label="dunning.name"
                        :value="dunning.id"
                    >
                    </el-option>
                </el-select>

                <span class="semibold dinlineb mt-2">
                    {{ $l10n.__('Plan de relance par défaut') }}
                </span>
            </p>

            <div class="dunning-header bb-2">
                <h3>
                    {{ $l10n.__('Plans de relance') }}
                </h3>
                <CompassButton
                    v-if="!planMaximumNumber || plans.length < planMaximumNumber"
                    :data-quota-guard="quotaGuard"
                    :label="$t.__('preferences.Dunning.Dunning.add_dunning_plan')"
                    type="secondary"
                    :disabled="isDisableLink"
                    class="mb-1"
                    @click="addPlan"
                />
            </div>

            <el-collapse v-model="defaultPlanPos" accordion class="b-1 br">
                <el-collapse-item
                    v-for="(plan, index) in plans"
                    :key="plan.id"
                    :title="plan.name"
                    :name="index"
                >
                    <plan :plan="plan" />
                </el-collapse-item>
            </el-collapse>
        </div>
    </div>
</template>

<script>
import Plan from './Plan.vue';
import DunningEnable from '../DunningEnable.vue';
import Store from 'stores/dunning';
import { createNamespacedHelpers } from 'vuex';
const { mapState, mapActions } = createNamespacedHelpers('dunning');

export default {
    components: {
        Plan,
        DunningEnable,
    },
    props: {
        doctype: {
            required: true,
            type: String,
        },
        dunnings: {
            required: true,
            type: Array,
        },
        hours: {
            required: true,
            type: Array,
        },
        weekDays: {
            required: true,
            type: Array,
        },
        daysList: {
            required: true,
            type: Array,
        },
        emailTemplate: {
            required: true,
            type: Array,
        },
        planMaximumNumber: {
            required: true,
            type: Number,
        },
        status: {
            required: true,
            type: Boolean,
        },
    },
    data() {
        return {
            defaultPlanId: 0,
            defaultPlanPos: 0,
            confirmationStatus: false,
            isDisableLink: true,
        };
    },
    computed: {
        /**
         * Return the context we need to check the quotas according to the doctype
         */
        quotaGuard() {
            return `${this.doctype}_dunning_plan`;
        },
        confirmationTitle() {
            if (this.enabled) {
                return this.$l10n.__('Désactivation');
            } else {
                return this.$l10n.__('Activation');
            }
        },
        confirmationMessage() {
            if (this.enabled) {
                return this.$l10n.__(
                    'Attention, vous allez désactiver la relance automatique pour ce type de document. Toutes les relances en cours seront arrêtées définitivement. Confirmez-vous la désactivation ?',
                );
            } else {
                return this.$l10n.__(
                    'Attention, vous allez activer la relance automatique pour ce type de document. Tous les nouveaux documents créés seront éligibles à la relance automatique en fonction de vos réglages.',
                );
            }
        },
        ...mapState(['plans', 'loading', 'enabled']),
    },
    created() {
        this.checkQuotas();

        this.$store.registerModule('dunning', Store);

        this.$store.commit('dunning/setDoctype', this.doctype);
        this.$store.commit('dunning/setEnabled', this.status);
        this.$store.commit('dunning/setPlans', this.dunnings);
        this.$store.commit('dunning/setHours', this.hours);
        this.$store.commit('dunning/setWeekDays', this.weekDays);
        this.$store.commit('dunning/setDaysList', this.daysList);
        this.$store.commit('dunning/setEmailTemplate', this.emailTemplate);

        this.defaultPlanId = this.getDefaultPlanId();
        this.defaultPlanPos = this.getDefaultPlanPos();
    },
    methods: {
        /**
         * Call window.Quota.checkQuotas
         * and pending the outcome, disable the associated button
         */
        checkQuotas() {
            this.isDisableLink = true;
            window.Quota.checkQuotas([this.quotaGuard]).then((_) => (this.isDisableLink = false));
        },
        setDefault(dunningId) {
            if (dunningId == 0) {
                this.setNoDefaultPlan();
                return;
            }
            const plan = this.plans.find((plan) => plan.id == dunningId);
            plan.main = true;
            this.updatePlan(plan);
        },
        getDefaultPlanId() {
            const plan = this.plans.find((plan) => plan.main);
            return plan ? plan.id : 0;
        },
        getDefaultPlanPos() {
            return this.plans.map((plan) => plan.id).indexOf(this.defaultPlanId);
        },
        addPlan(e) {
            // check if quota method have been added or if button have been desactivate
            // in this case do not continue
            if (!!e.target.onclick || this.isDisableLink) return;

            const plan = {
                main: false,
                enabled: false,
                name: this.$l10n.__('Nouveau plan de relance'),
                weekdays: [1, 2, 4, 8, 16],
                hours: [256, 512, 1024, 2048, 4096],
                recurring: false,
                gap: 2,
                steps: [],
            };
            this.createPlan(plan)
                .then(() => {
                    this.defaultPlanPos = this.plans.length - 1;
                    this.checkQuotas();
                })
                .catch((error) => {
                    Push.error(error.response.data.error.message);
                });
        },
        initFirstPlan() {
            this.initDunning().then(() => {
                this.defaultPlanId = this.getDefaultPlanId();
                this.defaultPlanPos = this.getDefaultPlanPos();
                this.checkQuotas();
            });
        },
        planEnableText(enabled) {
            return enabled ? this.$l10n.__('Activé') : this.$l10n.__('Désactivé');
        },
        planEnableColor(enabled) {
            return enabled ? 'chip-success' : 'chip-error';
        },
        confirmToggle() {
            this.toggleStatus().then(() => {
                this.confirmationStatus = false;
            });
        },
        ...mapActions([
            'createPlan',
            'updatePlan',
            'initDunning',
            'toggleStatus',
            'setNoDefaultPlan',
        ]),
    },
};
</script>

<style scoped lang="scss">
@import '~@sellsy/styleguide/src/_variables';

.dunning-modal {
    .dialog {
        &__default {
            padding-bottom: 24px;
            p {
                margin: 0;
            }
        }

        &__footer {
            display: flex;
            justify-content: flex-end;
            padding: 0 32px 24px 32px;
        }
    }
}
.dunning-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 4px;
}
.link.disabled {
    color: $grey !important;
    cursor: not-allowed;
}
</style>
